/**
 * OnboardingWizard — "Assistente de Boas-Vindas".
 * First login without a Product Catalog: asks "O que você vende?", saves the
 * keywords and immediately runs the USASpending backtest, showing the money
 * the user "left on the table" (locked winner fields drive the upgrade).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Plus, X, TrendingUp, ArrowRight, Rocket } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "alp.onboarding.dismissed";
const ADMIN_ROLES = new Set(["super_admin", "admin", "master", "master_demo"]);
const EXCLUDED = ["/login", "/signup", "/landing", "/trial-expired", "/billing",
  "/master", "/product-catalog", "/checkout"];

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)} bilhões`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)} milhões`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)} mil`;
  return `$${v.toFixed(0)}`;
};

export default function OnboardingWizard() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user || null;
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [chips, setChips] = useState([]);
  const [val, setVal] = useState("");
  const [running, setRunning] = useState(false);
  const [reach, setReach] = useState(null);

  useEffect(() => {
    if (!user || ADMIN_ROLES.has(user.role)) return;
    if (EXCLUDED.some((p) => location.pathname.startsWith(p))) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    let cancelled = false;
    axios.get(`${API}/product-catalog`).then(({ data }) => {
      if (!cancelled && !(data.keywords || []).length) setShow(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const addChip = () => {
    const v = val.trim();
    if (v.length >= 3 && !chips.includes(v)) setChips([...chips, v]);
    setVal("");
  };

  const runBacktest = async () => {
    const kws = val.trim().length >= 3 && !chips.includes(val.trim())
      ? [...chips, val.trim()] : chips;
    if (kws.length === 0) { toast.info("Digite pelo menos 1 produto"); return; }
    setChips(kws); setVal(""); setRunning(true);
    try {
      await axios.post(`${API}/product-catalog`, {
        keywords: kws, negative_keywords: [], hq_address: "",
        instant_enabled: true, digest_enabled: true,
        instant_min_value: 500000, instant_max_distance_mi: 150,
      });
      const { data } = await axios.post(`${API}/product-catalog/test-reach`,
        { keywords: kws, days: 30 });
      if (data.ok) { setReach(data); setStep(2); }
      else toast.error("Não foi possível consultar o histórico agora");
    } catch (_e) {
      toast.error("Falha na consulta — tente novamente");
    } finally { setRunning(false); }
  };

  const finish = (goCatalog) => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
    if (goCatalog) navigate("/product-catalog");
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="onboarding-wizard">
      <div className="bg-[var(--al-card-bg,white)] rounded-2xl border border-[var(--al-border)] shadow-2xl max-w-xl w-full p-7 relative">
        <button type="button" onClick={dismiss} data-testid="ow-dismiss"
          className="absolute top-4 right-4 text-[var(--al-text-muted)] hover:text-rose-500">
          <X className="w-5 h-5" />
        </button>

        {step === 1 && (
          <div data-testid="ow-step-1">
            <div className="inline-flex items-center gap-2 text-[#C9A24E] font-extrabold text-[12px] uppercase tracking-wider mb-2">
              <Rocket className="w-4 h-4" /> Bem-vindo ao AgenteLead Pro
            </div>
            <h2 className="text-[24px] font-extrabold leading-tight mb-1.5">
              O que você vende?
            </h2>
            <p className="text-[13px] text-[var(--al-text-muted)] mb-5">
              Digite seus produtos (em inglês, ex: <b>cement</b>, <b>PVC pipes</b>, <b>steel</b>) e
              descubra <b>quanto o governo americano comprou disso nos últimos 30 dias</b>.
            </p>
            <div className="flex gap-2 mb-3">
              <input value={val} onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChip(); } }}
                placeholder="Ex: cement, lumber, HVAC..."
                autoFocus data-testid="ow-input"
                className="flex-1 h-11 px-3 rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)] text-[13.5px] focus:border-[#C9A24E] outline-none" />
              <button type="button" onClick={addChip} data-testid="ow-add"
                className="h-11 w-11 rounded-lg border border-[var(--al-border)] inline-flex items-center justify-center hover:border-[#C9A24E]">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {chips.map((c) => (
                  <span key={c} className="text-[12px] px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-600 inline-flex items-center gap-1.5">
                    {c}
                    <button type="button" onClick={() => setChips(chips.filter((x) => x !== c))}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button type="button" onClick={runBacktest} disabled={running}
              data-testid="ow-run-btn"
              className="w-full h-12 rounded-xl bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[14px] font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
              {running
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Consultando USASpending.gov...</>)
                : (<><Sparkles className="w-4 h-4" /> Ver quanto o governo comprou disso</>)}
            </button>
            <button type="button" onClick={dismiss} data-testid="ow-skip"
              className="w-full mt-2 text-[11.5px] text-[var(--al-text-muted)] hover:underline">
              Agora não, explorar o app primeiro
            </button>
          </div>
        )}

        {step === 2 && reach && (
          <div data-testid="ow-step-2">
            <div className="inline-flex items-center gap-2 text-[#C9A24E] font-extrabold text-[12px] uppercase tracking-wider mb-2">
              <TrendingUp className="w-4 h-4" /> Resultado dos últimos 30 dias
            </div>
            <h2 className="text-[22px] font-extrabold leading-tight mb-3" data-testid="ow-result-headline">
              O governo comprou <span className="text-[#C9A24E]">{fmtMoney(reach.total_value_top100)}</span> dos
              seus produtos — em <span className="text-[#C9A24E]">{reach.contracts_found} contratos</span>.
            </h2>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mb-4">
              Isso é o que passou na sua frente só neste mês. Veja os 3 maiores:
            </p>
            <div className="space-y-2 mb-5">
              {(reach.top_contracts || []).map((c, i) => (
                <div key={`owc-${i}`} className="rounded-lg border border-[var(--al-border)] p-3 text-[12px]" data-testid={`ow-top-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="line-clamp-1 font-semibold flex-1">{c.title}</span>
                    <span className="font-extrabold text-[#1B3A8A] shrink-0">{fmtMoney(c.amount)}</span>
                  </div>
                  {c.locked ? (
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="select-none text-[11px]" style={{ filter: "blur(4px)" }} aria-hidden>
                        🏆 Winner Construction LLC · 🔗 link do edital
                      </span>
                      <span className="text-[10px] font-bold shrink-0">🔒 Ultimate</span>
                    </div>
                  ) : (
                    c.recipient && <div className="mt-1 text-[11px]">🏆 {c.recipient}</div>
                  )}
                </div>
              ))}
            </div>
            {reach.unlocked ? (
              <button type="button" onClick={() => finish(true)} data-testid="ow-cta-catalog"
                className="w-full h-12 rounded-xl bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[14px] font-extrabold inline-flex items-center justify-center gap-2">
                Configurar meu Catálogo de Produtos <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                <button type="button" data-testid="ow-cta-upgrade"
                  onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); navigate("/billing"); }}
                  className="w-full h-12 rounded-xl bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[14px] font-extrabold inline-flex items-center justify-center gap-2">
                  ⚡ Desbloquear ganhadores e editais — plano Ultimate
                </button>
                <button type="button" onClick={() => finish(true)} data-testid="ow-cta-catalog"
                  className="w-full mt-2 text-[12px] text-[var(--al-text-muted)] hover:underline">
                  Continuar no plano atual e configurar meu catálogo
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
